import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, Button } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { TenantName } from '../../../components/TenantName';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';

const MUTED = 'var(--rr-color-text-muted)';

/*
 * Build with AI conversations (owner's decision of 3 October 2026): every turn
 * customers had with the flow assistant in the last 30 days, newest first, so
 * its instructions can be tuned from what people really ask. Rows older than
 * 30 days are deleted by flows-llm, and customers are told so in the panel.
 */
export default function AssistantTranscriptsPage() {
  const t = useT();
  const q = useQuery({
    queryKey: ['admin', 'assistant-transcripts'],
    queryFn: () => flows.listAssistantTranscripts({ limit: 200 })
  });
  const rows = useMemo(() => q.data?.data ?? [], [q.data]);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const shown = onlyProblems ? rows.filter((r) => r.problems.length > 0 || !r.proposed_flow) : rows;
  const withProblems = rows.filter((r) => r.problems.length > 0).length;

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('assistantTranscripts.title')}
        description={t('assistantTranscripts.description')}
        actions={
          <Group gap="xs">
            <Button size="sm" variant={onlyProblems ? 'filled' : 'outline'} onClick={() => setOnlyProblems((v) => !v)} data-testid="transcripts-only-problems">
              {t('assistantTranscripts.onlyProblems')}
            </Button>
            <Button size="sm" variant="outline" loading={q.isFetching} onClick={() => void q.refetch()}>{t('assistantTranscripts.refresh')}</Button>
          </Group>
        }
      />
      {q.error && <Alert color="error" role="alert" title={t('assistantTranscripts.loadError')}><Text role="bodySmall">{(q.error as Error).message}</Text></Alert>}
      {!q.isPending && !q.error && (
        <Text role="bodySmall" style={{ color: MUTED }} data-testid="transcripts-summary">
          {t(rows.length === 1 ? 'assistantTranscripts.summaryOne' : 'assistantTranscripts.summary', { count: rows.length, problems: withProblems })}
        </Text>
      )}
      {!q.isPending && !q.error && shown.length === 0 && (
        <EmptyState title={t('assistantTranscripts.empty.title')} description={t('assistantTranscripts.empty.description')} />
      )}
      {shown.map((r) => (
        <Card key={r.id} withBorder data-testid="transcript">
          <Stack gap="xs">
            <Group gap="xs" justify="space-between">
              <Group gap="xs">
                <TenantName id={r.organisation_id} />
                <StatusBadge status={r.proposed_flow ? 'healthy' : 'inactive'}>
                  {t(r.proposed_flow ? 'assistantTranscripts.proposed' : 'assistantTranscripts.talkedOnly')}
                </StatusBadge>
                {r.problems.length > 0 && (
                  <StatusBadge status="down">{r.problems.length === 1 ? t('assistantTranscripts.problemOne') : t('assistantTranscripts.problemCount', { count: r.problems.length })}</StatusBadge>
                )}
              </Group>
              <Text role="caption" style={{ color: MUTED }}>
                {formatDateTime(r.created_at)}{r.model ? ` · ${r.model}` : ''} · {t('assistantTranscripts.tokens', { in: r.in_tokens, out: r.out_tokens })}
              </Text>
            </Group>
            <Text role="label">{t('assistantTranscripts.asked')}</Text>
            <Text role="bodySmall" style={{ whiteSpace: 'pre-wrap' }}>{r.conversation}</Text>
            <Text role="label">{t('assistantTranscripts.answered')}</Text>
            <Text role="bodySmall" style={{ whiteSpace: 'pre-wrap' }}>{r.reply}</Text>
            {r.problems.length > 0 && (
              <>
                <Text role="label">{t('assistantTranscripts.problems')}</Text>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {r.problems.map((p, i) => <li key={i}><Text role="bodySmall">{p}</Text></li>)}
                </ul>
              </>
            )}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
